import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { 
  FaLightbulb, FaHandshake, FaBullseye, FaStar,
  FaDesktop, FaCloud, FaRobot, FaShieldAlt,
  FaChartLine, FaSyncAlt, FaEye, FaCrosshairs,
  FaUserTie, FaCogs, FaDatabase, FaNetworkWired, FaCode, FaMobileAlt,
  FaBrain
} from 'react-icons/fa';
import './aboutus.css';

// Reusable component for animated elements on scroll
function AnimatedSection({ children, className }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10% 0px -10% 0px" });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7 }}
    >
      {children}
    </motion.div>
  );
}

function AboutUs() {
  const foundingYear = 2021;
  const currentYear = new Date().getFullYear();

  // Data
  // const values = [
  //   {
  //     title: "Innovation",
  //     description: "We challenge the status quo and explore cutting edge technologies to build what's next, not just what's now.",
  //     icon: <FaLightbulb className="glax-value-icon-svg" />
  //   },
  //   {
  //     title: "Collaboration",
  //     description: "Great ideas are born from diverse perspectives. We work closely with our clients as partners, not just vendors.",
  //     icon: <FaHandshake className="glax-value-icon-svg" />
  //   },
  //   {
  //     title: "Impact",
  //     description: "We measure our success by the tangible value and positive change we deliver to our clients' businesses.",
  //     icon: <FaBullseye className="glax-value-icon-svg" />
  //   },
  //   {
  //     title: "Excellence",
  //     description: "We're committed to delivering superior quality in everything we do, from code to customer service.",
  //     icon: <FaStar className="glax-value-icon-svg" />
  //   },
  // ];



  const values = [
  {
    number: "01",
    title: "Innovation",
    description: "We challenge the status quo and explore cutting edge technologies to build what's next, not just what's now.",
    icon: <FaLightbulb className="glax-value-icon-svg" />
  },
  {
    number: "02",
    title: "Collaboration",
    description: "Great ideas are born from diverse perspectives. We work closely with our clients as partners, not just vendors.",
    icon: <FaHandshake className="glax-value-icon-svg" />
  },
  {
    number: "03",
    title: "Impact",
    description: "We measure our success by the tangible value and positive change we deliver to our clients' businesses.",
    icon: <FaBullseye className="glax-value-icon-svg" />
  },
  {
    number: "04",
    title: "Excellence",
    description: "We're committed to delivering superior quality in everything we do, from code to customer service.",
    icon: <FaStar className="glax-value-icon-svg" />
  },
];

  const milestones = [
    { year: 2021, event: "GLAXMORE Founded", description: "Established with a vision to revolutionize IT solutions through innovation." },
    { year: 2022, event: "First Enterprise Client", description: "Secured partnership with a Fortune companies, validating our approach." },
    { year: 2023, event: "Team Expansion", description: "Grew our team to over 15 technology experts and industry specialists." },
    { year: 2024, event: "AI & Cloud Division", description: "Launched specialized practices in AI, Machine Learning and Cloud solutions." },
  ];

  const services = [
    { name: "Web Development", icon: <FaCode className="glax-service-icon-svg" /> },
    { name: "Custom Software Development", icon: <FaDesktop className="glax-service-icon-svg" /> },
    { name: "Mobile App Development", icon: <FaMobileAlt className="glax-service-icon-svg" /> },
    { name: "Digital Transformation", icon: <FaSyncAlt className="glax-service-icon-svg" /> },
    { name: "IT Consulting", icon: <FaChartLine className="glax-service-icon-svg" /> },
    { name: "AI & Machine Learning", icon: <FaBrain className="glax-service-icon-svg" /> },

  ];

  return (
    <div className="glax-about-container">
      {/* ===== HERO HEADER ===== */}
      <section className="glax-about-hero">
        <motion.div 
          className="glax-about-hero-content"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h2>Transforming Businesses <span className="glax-gradient-text">Through Innovative Technology</span></h2>
          <p className="glax-hero-subtitle">
            Since {foundingYear}, GLAXMORE has been at the forefront of delivering cutting edge IT solutions that drive growth, efficiency, and competitive advantage for businesses worldwide. Our team of {currentYear - foundingYear > 2 ? '15+' : 'highly skilled'} experts combines technical excellence with deep industry knowledge to create digital solutions that matter.
          </p>
        </motion.div>
        <motion.div 
          className="glax-hero-graphic"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          <div className="glax-tech-graphic">
            <div className="glax-floating-element glax-element-1">
              <FaBrain className="glax-floating-icon" />
            </div>
            <div className="glax-floating-element glax-element-2">
              <FaCloud className="glax-floating-icon" />
            </div>
            <div className="glax-floating-element glax-element-3">
              <FaDatabase className="glax-floating-icon" />
            </div>
            <div className="glax-floating-element glax-element-4">
              <FaNetworkWired className="glax-floating-icon" />
            </div>
            <div className="glax-main-circle">
              <div className="glax-inner-circle">
                <FaCogs className="glax-contact-main-icon" />
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ===== VISION & MISSION SECTION ===== */}
      <section className="glax-vision-mission-section">
        <div className="glax-vm-container">
          <AnimatedSection className="glax-vision-card glax-vm-card">
            <div className="glax-vm-icon">
              <FaEye className="glax-vm-icon-svg" />
            </div>
            <h3>Our Vision</h3>
            <p>"To inspire innovation and lead the way in creating smarter IT solutions for a better tomorrow."</p>
          </AnimatedSection>
          
          <AnimatedSection className="glax-mission-card glax-vm-card">
            <div className="glax-vm-icon">
              <FaCrosshairs className="glax-vm-icon-svg" />
            </div>
            <h3>Our Mission</h3>
            <p>"Delivering reliable, innovative, and cost effective IT solutions that empower businesses and communities."</p>
          </AnimatedSection>
        </div>
      </section>

      {/* ===== SERVICES SECTION ===== */}
      <section className="glax-services-section">
        <AnimatedSection>
          <h2>Our Expertise</h2>
          <p className="glax-section-subtitle">Comprehensive IT solutions tailored to your business needs</p>
        </AnimatedSection>
        <div className="glax-services-grid">
          {services.map((service, index) => (
            <AnimatedSection key={index} className="glax-service-item">
              <motion.div 
                className="glax-service-icon"
                whileHover={{ rotate: 5, scale: 1.1 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                {service.icon}
              </motion.div>
              <h4>{service.name}</h4>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* ===== VALUES SECTION ===== */}
      <section className="glax-values-section">
        <AnimatedSection>
          <h2>Our Core Values</h2>
          <p className="glax-section-subtitle">The principles that guide everything we do</p>
        </AnimatedSection>
        <div className="glax-values-grid">
          {values.map((value, index) => (
            <AnimatedSection key={index} className="glax-value-card">
              <motion.div 
                className="glax-value-icon"
                whileHover={{ rotate: 5, scale: 1.1 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                {value.icon}
              </motion.div>
              <h3>{value.title}</h3>
              <p>{value.description}</p>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* ===== TIMELINE SECTION ===== */}
      <section className="glax-timeline-section">
        <AnimatedSection>
          <h2>Our Journey</h2>
          <p className="glax-section-subtitle">Key milestones in our growth story</p>
        </AnimatedSection>
        <div className="glax-timeline">
          {milestones.map((milestone, index) => (
            <AnimatedSection key={index} className={`glax-timeline-item ${index % 2 === 0 ? 'glax-left' : 'glax-right'}`}>
              <div className="glax-timeline-content">
                <motion.div 
                  className="glax-timeline-year"
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ type: "spring", stiffness: 100, delay: 0.2 }}
                >
                  {milestone.year}
                </motion.div>
                <h4>{milestone.event}</h4>
                <p>{milestone.description}</p>
              </div>
              <div className="glax-timeline-marker"></div>
            </AnimatedSection>
          ))}
          <div className="glax-timeline-line"></div>
        </div>
      </section>

      {/* ===== TEAM SECTION =====
      <section className="glax-team-section">
        <AnimatedSection>
          <h2>Our Leadership</h2>
          <p className="glax-section-subtitle">Experienced professionals guiding our vision</p>
        </AnimatedSection>
        <div className="glax-team-grid">
          <AnimatedSection className="glax-team-member">
            <div className="glax-member-photo"></div>
            <h4>Sarah Johnson</h4>
            <p className="glax-role">CEO & Founder</p>
            <p>15+ years in technology leadership</p>
          </AnimatedSection>
          <AnimatedSection className="glax-team-member">
            <div className="glax-member-photo"></div>
            <h4>Michael Chen</h4>
            <p className="glax-role">CTO</p>
            <p>Expert in AI and cloud architecture</p>
          </AnimatedSection>
          <AnimatedSection className="glax-team-member">
            <div className="glax-member-photo"></div>
            <h4>Priya Sharma</h4>
            <p className="glax-role">VP of Operations</p>
            <p>Driving excellence in delivery</p>
          </AnimatedSection>
        </div>
      </section> */}

      {/* ===== CTA SECTION ===== */}
      <AnimatedSection className="glax-about-cta">
        <div className="glax-cta-content">
          <h2>Ready to Transform Your Business?</h2>
          <p>Let's discuss how GLAXMORE can help you achieve your technology goals.</p>
          <a href='/contactus' style={{ textDecoration: 'none' }}>
          <motion.button 
            className="glax-btn glax-btn-primary"
            whileHover={{ scale: 1.05, boxShadow: "0 10px 25px rgba(0, 0, 0, 0.4)" }}           
            whileTap={{ scale: 0.95 }}
          >
            Get in Touch
          </motion.button></a>
        </div>
      </AnimatedSection>
    </div>
  );
}

export default AboutUs;